import { type Faction, RACES, type Race, type Unit } from "@sins2-atlas/game-data";

// The Faction Selection: the set of Factions the whole site is showing (ADR 0010,
// CONTEXT.md: Faction Selection). It lives HERE rather than in lib/roster-filters.ts —
// which is per-view by name and by contract — because the shell, both roster views,
// /tech, /compare and the unit search all read it.
//
// It is a set, never a single value: ticking a Race ticks its Factions, so a Race is a
// group tick and Race and Faction stay as strictly separated as the glossary has them. The
// full set is **All Factions**, the default, and the EMPTY set is No Factions, a state the
// reader can reach and stay in (user decision 2026-09-06): unticking the last entry used to
// snap back to All Factions, which read as the control fighting the reader, and the All
// Factions row itself now unticks. Every surface already handled a selection that matches
// nothing — that is what an unrecognized token produces — so No Factions renders as the
// pages' existing empty state, with its own way back.
//
// In the URL it is `?faction=`, the comma-separated list of the game's own short names,
// absent at All Factions (ADR 0002: the schema is a public API, and a canonical link is
// short) and the literal `none` at No Factions, which has to be spelled because an absent
// key already means the opposite. `?race=` is retired: a Race tick serializes as its Factions.

// Faction tokens are the words the UI actually SHOWS for each faction, lowercased —
// the in-game short names for the six two-faction entries (player_faction_short_name.*;
// loyalist/rebel are data-file ids the UI never displays). The Eidolon (2.0.2.0) have ONE
// Faction whose displayed name is the race's own ("Eidolon", player_faction_name); its
// short name "Herald" appears on no surface (the exclusivity chip never renders for a
// one-faction race), so a `herald` token would be undiscoverable and the unknown-token
// notice would endorse a word ("Eidolon") the param rejects — the review round caught
// exactly that self-defeating message. The token is therefore `eidolon`, sharing the
// race's own name the way the faction does.
export const FACTION_TOKENS = [
  { token: "enclave", race: "tec", faction: "loyalist", label: "TEC Enclave" },
  { token: "primacy", race: "tec", faction: "rebel", label: "TEC Primacy" },
  { token: "wrath", race: "advent", faction: "loyalist", label: "Advent Wrath" },
  { token: "reborn", race: "advent", faction: "rebel", label: "Advent Reborn" },
  { token: "exodus", race: "vasari", faction: "loyalist", label: "Vasari Exodus" },
  { token: "alliance", race: "vasari", faction: "rebel", label: "Vasari Alliance" },
  { token: "eidolon", race: "eidolon", faction: "loyalist", label: "Eidolon" },
] as const satisfies ReadonlyArray<{
  token: string;
  race: Race;
  faction: Faction;
  label: string;
}>;
export type FactionToken = (typeof FACTION_TOKENS)[number]["token"];
export type FactionEntry = (typeof FACTION_TOKENS)[number];

/** The faction tokens alone, in canonical order — parse, serialize and the toggles all need
 * the bare list, and deriving it at each use site rebuilt it on every render. */
export const FACTION_TOKEN_LIST: readonly FactionToken[] = FACTION_TOKENS.map(
  (entry) => entry.token,
);

// Sins I's faction names, which Sins II retired for the short names above but which its
// data files still use as internal ids (`*_loyalist` / `*_rebel`, CONTEXT.md: Faction). They
// are therefore the likeliest wrong guess in a hand-written or inherited link — recognized
// only so the page can name them as Sins I terms, NEVER accepted as a selection. They could
// not be accepted anyway: each maps to three different Sins II Factions.
const SINS1_FACTION_TOKENS: readonly string[] = ["loyalist", "rebel"];

/** Which of these unrecognized tokens are retired Sins I faction names, in the given order.
 * Case-insensitive: the tokens themselves are lowercase, but someone typing this name by hand
 * is copying it from the game's own capitalized "Rebel", and this only decides whether the
 * page offers the Sins I explanation — never whether a token is accepted. */
export function sins1FactionTokens(tokens: readonly string[]): string[] {
  return tokens.filter((token) => SINS1_FACTION_TOKENS.includes(token.toLowerCase()));
}

/** The aside explaining that an unrecognized faction token is a Sins I name, or undefined
 * when none of them is. Shared so every surface words it identically. */
export function sins1FactionNote(tokens: readonly string[]): string | undefined {
  const retired = sins1FactionTokens(tokens).map(
    (token) => token.slice(0, 1).toUpperCase() + token.slice(1),
  );
  if (retired.length === 0) return undefined;
  const verb = retired.length > 1 ? "are Sins I names" : "is a Sins I name";
  return `${retired.join(" and ")} ${verb}; Sins II gives its Factions their own names.`;
}

export interface FactionSelection {
  /** The selected Factions in canonical order. All seven of them IS All Factions. */
  factions: FactionToken[];
  /** The tokens the URL named that no Faction answers to, in the order the URL gave them —
   * shown back to the reader verbatim (ADR 0002 amendment). A selection asked for with only
   * unrecognizable tokens is still ACTIVE and matches nothing, so an unknown token never
   * widens the result back to All Factions. */
  unknown: string[];
}

/** The whole set: the default, and what the All Factions row ticks ON. */
export const ALL_FACTIONS: FactionSelection = {
  factions: [...FACTION_TOKEN_LIST],
  unknown: [],
};

/** The empty set: what the All Factions row unticks TO, and where unticking the last entry
 * leaves the reader. It is a selection like any other — active, and matching nothing. */
export const NO_FACTIONS: FactionSelection = { factions: [], unknown: [] };

/** The one word `?faction=` spells for the empty set. It cannot be the absent key (that is
 * All Factions) and it cannot be the empty string (a stray `?faction=` is a malformed link,
 * not a request to see nothing). No Faction answers to it, so it can never collide. */
const NO_FACTIONS_TOKEN = "none";

export interface FactionSearch {
  faction?: string;
}

export function isAllFactions(selection: FactionSelection): boolean {
  return selection.unknown.length === 0 && selection.factions.length === FACTION_TOKENS.length;
}

/** Whether this selection names no Faction at all — however it got there: the reader unticked
 * the last one, or the URL said `none`, or it named only tokens no Faction answers to. All
 * three show the same page, and the pages already say so. */
export function isNoFactions(selection: FactionSelection): boolean {
  return selection.factions.length === 0;
}

export function parseFactionSelection(search: FactionSearch): FactionSelection {
  const value = search.faction;
  if (value === undefined || value === "") return ALL_FACTIONS;
  if (value === NO_FACTIONS_TOKEN) return NO_FACTIONS;
  const requested = new Set(value.split(","));
  const factions = FACTION_TOKEN_LIST.filter((token) => requested.has(token));
  const unknown = [...requested].filter(
    (token) => token !== "" && !FACTION_TOKEN_LIST.includes(token as FactionToken),
  );
  // A param that named nothing at all (a stray comma) named no filter either.
  if (factions.length === 0 && unknown.length === 0) return ALL_FACTIONS;
  return { factions, unknown };
}

/** Does this unit belong to any selected Faction? The rule is the one the roster's faction
 * facet already carried: a token names a Race AND one of its Factions, so Enclave never
 * matches an Advent unit and never matches a Primacy-only TEC hull. */
export function matchesFactionSelection(
  unit: Pick<Unit, "race" | "factions">,
  selection: FactionSelection,
): boolean {
  return FACTION_TOKENS.some(
    (entry) =>
      selection.factions.includes(entry.token) &&
      entry.race === unit.race &&
      unit.factions.includes(entry.faction),
  );
}

/** The selection as it rides a link, read straight off the search it is leaving: parsed and
 * re-serialized, so All Factions stays out of the URL and the order stays canonical. Every
 * unit-level link spreads this the way it spreads the stat frame (ADR 0010). */
export function factionLinkSearch(search: FactionSearch): { faction?: string } {
  return serializeFactionSelection(parseFactionSelection(search));
}

/** The selection as search params. All Factions writes nothing — it is the default, and a
 * canonical link is short. The empty set writes `none`: an absent key would silently widen a
 * reader who is deliberately looking at nothing back to everything.
 *
 * The one empty set that writes NOTHING is the one an unrecognized token produced. That token
 * is reported on the page the reader is standing on and does not outlive the click that leaves
 * it, so there is no chosen emptiness to carry — only a mistake in a link, which the next page
 * should not inherit as a deliberate No Factions. */
export function serializeFactionSelection(selection: FactionSelection): { faction?: string } {
  if (isAllFactions(selection)) return { faction: undefined };
  if (isNoFactions(selection)) {
    return { faction: selection.unknown.length > 0 ? undefined : NO_FACTIONS_TOKEN };
  }
  return { faction: selection.factions.join(",") };
}

/** That Race's Factions, in canonical order — the picker's second level, and what a Race
 * tick ticks. */
export function factionsOfRace(race: Race): readonly FactionEntry[] {
  return FACTION_TOKENS.filter((entry) => entry.race === race);
}

/** The Races a selection names, in the dataset's own Race order. Research belongs to a Race,
 * not to a Faction (CONTEXT.md: Domain), so this is the question `/tech` and the Tech picker
 * ask the selection: either TEC Faction, or both, asks for exactly the same TEC rows. A
 * selection only unrecognized tokens asked for names no Race — it is active and matches
 * nothing, and the surfaces say so rather than widening back to All Factions. */
export function racesOf(selection: FactionSelection): Race[] {
  return RACES.filter((race) =>
    factionsOfRace(race).some((entry) => selection.factions.includes(entry.token)),
  );
}

/** Whether a Race's Factions are all, partly, or not selected — what the picker's Race row
 * shows, and what decides which way its tick goes. */
export function raceTickState(selection: FactionSelection, race: Race): "all" | "some" | "none" {
  const selected = factionsOfRace(race).filter((entry) => selection.factions.includes(entry.token));
  if (selected.length === 0) return "none";
  return selected.length === factionsOfRace(race).length ? "all" : "some";
}

/** Build a selection from a token set: canonical order, and no unknowns (a tick answers the
 * URL's unrecognized tokens, so they must not outlive it). An empty set stays empty — the
 * picker means what the reader clicked, and the pages carry their own way back. */
function selectionOf(tokens: Iterable<FactionToken>): FactionSelection {
  const wanted = new Set(tokens);
  return { factions: FACTION_TOKEN_LIST.filter((token) => wanted.has(token)), unknown: [] };
}

/** Tick one Faction on or off. */
export function toggleFaction(selection: FactionSelection, token: FactionToken): FactionSelection {
  const next = new Set(selection.factions);
  if (next.has(token)) next.delete(token);
  else next.add(token);
  return selectionOf(next);
}

/** Tick a whole Race: its Factions go on together, and off together once they are all on. */
export function toggleRace(selection: FactionSelection, race: Race): FactionSelection {
  const next = new Set(selection.factions);
  const owned = factionsOfRace(race);
  if (raceTickState(selection, race) === "all") {
    for (const entry of owned) next.delete(entry.token);
  } else {
    for (const entry of owned) next.add(entry.token);
  }
  return selectionOf(next);
}
